import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const SECTIONS = [
  {
    title: '1. Information We Collect',
    content: `We collect information you provide directly to us when you:
• Create an account (name, email address, phone number)
• List a property (property details, images, location)
• Post a rental requirement (name, phone, preferences)
• Contact a property owner or tenant
• Use our messaging feature

We also automatically collect certain information when you use our services, including device information, IP address, browser type, pages visited, and usage patterns through analytics tools (Google Analytics, internal analytics).`,
  },
  {
    title: '2. How We Use Your Information',
    content: `We use the information we collect to:
• Create and manage your account
• Match tenants with relevant properties and owners
• Facilitate communication between tenants and property owners/brokers
• Send notifications about your listings, requirements, and messages
• Improve our platform and user experience
• Comply with legal obligations
• Prevent fraud and ensure platform security`,
  },
  {
    title: '3. Information Sharing',
    content: `We do not sell your personal information. We share your information only in these circumstances:
• With other users as needed (e.g., owners can see tenant contact details when a tenant posts a requirement)
• With service providers who help us operate our platform (SMS gateway, email service)
• When required by law or to protect rights and safety
• With your consent

Property listings and rental requirements you post are visible to other users of the platform.`,
  },
  {
    title: '4. Data Storage & Security',
    content: `Your data is stored on secure servers. We implement industry-standard security measures including:
• Encrypted data transmission (HTTPS/TLS)
• Secure session management
• Regular security audits

However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.`,
  },
  {
    title: '5. Your Rights & Choices',
    content: `You have the right to:
• Access your personal information
• Update or correct your information via your dashboard
• Delete your account and associated data by contacting us
• Opt out of promotional communications
• Request a copy of your data

To exercise these rights, contact us at the email below.`,
  },
  {
    title: '6. Cookies & Tracking',
    content: `We use cookies and similar technologies to:
• Keep you logged in
• Remember your preferences
• Analyse how our platform is used (Google Analytics, GA4 ID: G-EZBLEF6VWN)

You can control cookies through your browser settings. Disabling cookies may affect some features.`,
  },
  {
    title: '7. Third-Party Services',
    content: `Our platform uses the following third-party services:
• Google Analytics — website traffic analysis
• Fast2SMS — SMS/OTP delivery
• Twilio — WhatsApp notifications
• Resend — Email delivery
• Google Sign-In — Authentication

These services have their own privacy policies. We encourage you to review them.`,
  },
  {
    title: '8. Children\'s Privacy',
    content: `Rent Mitra is not directed at children under 18 years of age. We do not knowingly collect personal information from children under 18. If you believe a child has provided us with personal information, please contact us immediately.`,
  },
  {
    title: '9. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice on our platform or sending an email. Your continued use of Rent Mitra after changes constitutes acceptance of the updated policy.`,
  },
  {
    title: '10. Contact Us',
    content: `If you have questions about this Privacy Policy or our data practices, please contact us:

Email: support@rentmitrahub.com
Website: https://rentmitrahub.com
Address: Vadodara, Gujarat, India`,
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Privacy Policy | Rent Mitra</title>
        <meta name="description" content="Privacy Policy for Rent Mitra — India's rental property portal." />
        <link rel="canonical" href={`${window.location.origin}/privacy-policy`} />
      </Helmet>
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="text-blue-700 text-sm font-medium hover:underline">← Back to Home</Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-4 mb-2" style={{fontFamily:'Outfit,sans-serif'}}>
            Privacy Policy
          </h1>
          <p className="text-slate-500 text-sm">
            Last updated: April 2026 &nbsp;·&nbsp; Effective date: April 2026
          </p>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Welcome to Rent Mitra (<strong>rentmitrahub.com</strong>). This Privacy Policy explains how we collect,
            use, and protect your personal information when you use our rental property platform.
            By using Rent Mitra, you agree to the practices described in this policy.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {SECTIONS.map(s => (
            <div key={s.title} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <h2 className="font-bold text-slate-900 text-base mb-3" style={{fontFamily:'Outfit,sans-serif'}}>{s.title}</h2>
              <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{s.content}</p>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-slate-400 mt-10">
          © {new Date().getFullYear()} Rent Mitra. All rights reserved.
        </p>
      </div>

      <Footer />
    </div>
  );
}
